<template>
    <div class="video_wrapper" :style="{zIndex}">
        <video ref="video" playsinline="1" preload="auto" :muted="autoplaying" disablePictureInPicture></video>
    </div>
</template>

<script>
import { debug, warning, error } from '@lib/logger';
import Hls from 'hls.js';
import { mapMutations, mapGetters, mapActions } from 'vuex';
import { EventBus } from '@lib/EventBus';
import handleAction from '@mixins/HandleAction';

export default {
    name: 'TheVideo',

    props: {
        id: {
            type: Number,
            required: true,
            description: 'ID of the video'
        },
        startVideo: {},
        zIndex: {
            type: Number,
            description: 'Stack order of the video',
            required: true
        }
    },
    mixins: [handleAction],
    computed: {
        ...mapGetters([
            'activeVideoId',
            'playing',
            'played',
            'volume',
            'muted',
            'videos',
            'autoplaying',
            'project',
            'node'
        ]),
        video() {
            return this.videos[this.id];
        },
        canPlayNative() {
            return this._videoEl.canPlayType('application/vnd.apple.mpegurl');
        },
        active() {
            return this.activeVideoId == this.id;
        },
        hasActionOnCompletion() {
            return this.node.completeAction && this.node.completeActionArg;
        },
        hasEnabledInteractionLayer() {
            return (
                this.node.interaction_layer_id &&
                this.node.enable_interaction_layer
            );
        }
    },

    mounted() {
        this._videoEl = this.$refs.video;
        // this._videoEl.disablePictureInPicture();
        this.startLoad();

        EventBus.$on('skipTo', time => {
            if (this.active) {
                this.ended = false;
                this._videoEl.currentTime = time;
            }
        });

        EventBus.$on('setVolume', val => {
            this.setVolume(val);
        });
    },

    data: () => ({
        hls: false,
        ended: false,
        // set 9 different times as an array
        analytics_percentages: [],

        // the index of the next analytics percentage to fire
        analytics_fired: 0
    }),

    methods: {
        ...mapActions(['endTransition']),
        ...mapMutations([
            'updateCurrentTime',
            'updateVideoDuration',
            'pause',
            'play',
            'setBuffering',
            'showCantPlayMsg'
        ]),

        setAnalyticsPercentages(duration) {
            const onePercent = duration / 100;
            this.analytics_percentages = [
                onePercent * 10,
                onePercent * 20,
                onePercent * 30,
                onePercent * 40,
                onePercent * 50,
                onePercent * 60,
                onePercent * 70,
                onePercent * 80,
                onePercent * 90
            ];
        },

        startLoad() {
            if (Hls.isSupported()) {
                this.loadHls();
            } else if (this.canPlayNative) {
                this.loadNative();
            } else {
                this.cantPlayVideo();
            }
        },

        loadHls() {
            debug('Video ' + this.video.id + ' loading source via Hls.js...');
            this.hls = new Hls();
            this.hls.attachMedia(this._videoEl);
            this.hls.loadSource(this.video.manifest_url);

            this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                // console.log('manifest data ........', data);
                this.videoReady();
            });
            // this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            //     // this.hls.on(Hls.Events.)
            // });

            // this.hls.on(Hls.Events.ERROR, function(event, data) {
            //     // console.warn('Hls errored ..........\n', data);
            // });
        },

        loadNative() {
            debug('Loading source natively...');

            this._videoEl.src = this.video.manifest_url;
            this._videoEl.addEventListener('loadedmetadata', () => {
                this.videoReady();
            });
        },

        cantPlayVideo() {
            // todo Do something here to show error to user
            this.showCantPlayMsg();
        },

        videoReady() {
            if (isNaN(this._videoEl.duration)) {
                // IF we don't have the duration yet try again in .2s
                // we can't say the video is ready until this has been
                // loaded in because the timeline wont work.
                setTimeout(() => {
                    this.videoReady();
                }, 200);
                return;
            }

            debug('Video ' + this.video.id + ' is ready to play...');
            this.endTransition(this.video.id);

            if (this.active) {
                this.updateVideoDuration(this._videoEl.duration);
            }
            this.setAnalyticsPercentages(this._videoEl.duration);

            // Events that only make sense when video is ready
            if (this.hasEnabledInteractionLayer) {
                // this timer is triggered from the interaction layer timer when it's done
                EventBus.$on('INTERACTION_LAYER_TIMER_DONE', nodeId => {
                    // check the interaction layer which triggered this is actually for this node
                    if (this.node.id == nodeId) {
                        this.handleNodeCompletion();
                    }
                });
            }

            this._videoEl.addEventListener('waiting', e => {
                if (this.active) {
                    this.setBuffering(true);
                }
            });
            this._videoEl.addEventListener('playing', e => {
                if (this.active) {
                    this.setBuffering(false);
                }
            });
            this._videoEl.addEventListener('stalled', e => {
                if (this.active) {
                    // console.warn('stalled........')
                }
            });
            this._videoEl.addEventListener('timeupdate', event => {
                if (this.active) {
                    this.updateCurrentTime(event.target.currentTime);

                    if (
                        event.target.currentTime >
                        this.analytics_percentages[this.analytics_fired]
                    ) {
                        const percent = (this.analytics_fired + 1) * 10;
                        window.analytics.nodeViewDuration(
                            percent + '%',
                            this.node.id
                        );
                        this.analytics_fired += 1;
                    }
                }
            });

            this._videoEl.addEventListener('ended', event => {
                this.ended = true;
                if (this.active) {
                    // update playing state , ⚡️ crucial for few places 
                    this.pause();
                    // Analytics should fire regardless of IL
                    window.fbPixel.nodeEnded(this.node, this.video);
                    window.analytics.nodeViewDuration('100%', this.node.id);

                    // Other stuff elsewhere relies on this event , like the interaction layer if any etc...
                    EventBus.$emit('nodeEnded', this.node.id);

                    // directly fire node end action or loop if no IL is setup
                    // if there is one setup , the node completion stuff will trigger from
                    // an eventListener listening for the IL's timer to finish
                    if (!this.hasEnabledInteractionLayer)
                        this.handleNodeCompletion();
                }
            });

            if (
                this.project.autoplay &&
                this.startVideo === this.video.id &&
                this.autoplaying
            ) {
                this._videoEl.play();
            }
        },

        togglePlaying(newVal) {
            if (newVal) {
                if (this.ended) {
                    this.pause();
                } else {
                    // this may be triggered as a reaction to vuex for an element already playing
                    if (this._videoEl.paused) this._videoEl.play();
                }
            } else {
                this._videoEl.pause();
            }
        },
        /** Handles node related end actions , action on complete or looping etc... */
        handleNodeCompletion() {
            if (this.node.loop) {
                this.ended = false;
                this._videoEl.currentTime = 0;
                this._videoEl.play();
                this.play()
            }
            // if shouldn't loop then do end action if present
            else if (this.hasActionOnCompletion) {
                this.handleNodeCompleteAction(); // in the handleAction mixin
            }
        },
        setVolume(newVal) {
            this._videoEl.volume = newVal;
        },
        setMuted(newVal) {
            this._videoEl.muted = newVal;
        }
    },

    watch: {
        playing(newVal) {
            // console.log('playing state changed..........', newVal);
            if (this.active) {
                this.togglePlaying(newVal);
            }
        },
        volume(newVal) {
            this.setVolume(newVal);
        },
        muted(newVal) {
            this.setMuted(newVal);
        },
        active(newVal) {
            if (newVal) {
                this.updateVideoDuration(this._videoEl.duration);
                if (this.played) {
                    // prevent playing when the first video is loaded in
                    this._videoEl.play();
                    // we must update vuex here , this gets triggered mostly on node change
                    // handle the vuex incomming update too so it doesn't play an already playing vid
                    this.play();

                    window.fbPixel.nodePlay(this.node, this.video);
                }
            } else {
                this._videoEl.pause();

                // Move video back to the start when we move to a new video
                setTimeout(() => {
                    this._videoEl.currentTime = 0;
                    this.ended = false;

                    // Reset the analytics to false in case it's played again
                    this.analytics_fired = 0;
                }, 300);
            }
        }
    },

    beforeDestroy() {
        // destroy hls.js object if not native 
        if(this.hls) this.hls.destroy();
    }
}
</script>

<style lang="scss" scoped>
video {
    width: 100% !important;
    height: 100% !important;
    /* z-index: -1;
    opacity: 0;
    transition: 0.2s linear opacity; */
    position: absolute;
    top: 0;
    left: 0;
}
.video_wrapper {
    width: 100% !important;
    height: 100% !important;
    transition: 0.2s linear opacity;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 1;
}
</style>